import type { Identifier } from "ra-core";

import type {
  QuoterConfig,
  QuoterCustomPlanInput,
  QuoterModel,
  QuoterModelComparison,
  QuoterModelListPrice,
  QuoterPanel,
  QuoterPlan,
  QuoterPreview,
  QuoterPriceList,
  QuoterScheme,
  QuoterUnitDetail,
  Unit,
} from "../types";
import {
  addDays,
  buildPlan,
  cleanAmount,
  roundHalfUp,
  validateCustomPlan,
} from "./quoterCalendar";

/**
 * Almikia Epic EQ2 (TASK-098): the pure TypeScript mirror of the SQL quote
 * engine (`quoter_list_*`, `quoter_calc`, `quoter_resolve_plan`,
 * `quoter_compute_for_unit`, `quoter_panel`). Ported from the owner's
 * reference quoter (listaVigente, precioBase, precioLote). The SQL (numeric)
 * is authoritative; this mirror feeds the demo (FakeRest) and the UI preview,
 * and the parity tests prove both give the same numbers. Pure functions, no
 * I/O: a preview recomputes synchronously. Rounding happens ONLY where the
 * reference rounds (list price, excess, final price), never per step.
 */

/** Everything the engine needs about ONE project. */
export type QuoterProjectData = {
  config: QuoterConfig;
  models: QuoterModel[];
  priceLists: QuoterPriceList[];
  listPrices: QuoterModelListPrice[];
  schemes: QuoterScheme[];
};

/** Units of the project with their quoter details (for the sales count and scarcity). */
export type QuoterUnitsData = {
  units: Unit[];
  details: QuoterUnitDetail[];
};

const activeLists = (priceLists: QuoterPriceList[]) =>
  priceLists
    .filter((list) => !list.archived_at)
    .sort((a, b) => a.list_number - b.list_number);

/**
 * Sales needed to reach each active list: the running sum of the block sizes
 * of the lists before it (list 1 needs 0).
 */
export const listThresholds = (
  priceLists: QuoterPriceList[],
): { list_number: number; sales_needed: number }[] => {
  let running = 0;
  return activeLists(priceLists).map((list) => {
    const threshold = { list_number: list.list_number, sales_needed: running };
    running += list.block_size;
    return threshold;
  });
};

/**
 * `listaVigente`: the highest list whose threshold is reached, capped at the
 * last list. Null when there is no active list.
 */
export const listForSales = (
  priceLists: QuoterPriceList[],
  sales: number,
): number | null => {
  const reached = listThresholds(priceLists).filter(
    (threshold) => threshold.sales_needed <= sales,
  );
  return reached.length ? reached[reached.length - 1].list_number : null;
};

/** Units that count toward the list: sold, plus reserved when so configured. */
export const salesCount = (
  units: Unit[],
  config: Pick<QuoterConfig, "apartados_count_toward_list">,
): number =>
  units.filter(
    (unit) =>
      unit.status === "vendida" ||
      (unit.status === "apartada" && config.apartados_count_toward_list),
  ).length;

/**
 * `precioBase`: the model's price on a list. Walks the active lists up to
 * `list`: a captured price replaces the running value, otherwise the running
 * value is multiplied by (1 + increment/100). The product stays unrounded
 * and is rounded ONCE at the end, so a uniform increment equals
 * round(base * (1 + e)^(list - 1)).
 */
export const listPrice = (
  model: Pick<QuoterModel, "id" | "base_price">,
  priceLists: QuoterPriceList[],
  listPrices: QuoterModelListPrice[],
  list: number,
): number => {
  let current = model.base_price;
  for (const priceList of activeLists(priceLists)) {
    if (priceList.list_number > list) break;
    const captured = listPrices.find(
      (row) => row.price_list_id === priceList.id && row.model_id === model.id,
    );
    if (captured) {
      current = captured.price;
    } else if (priceList.list_number > 1) {
      current *= 1 + (priceList.increment_pct ?? 0) / 100;
    }
  }
  return roundHalfUp(current);
};

type ResolvePlanInput = {
  schemes: QuoterScheme[];
  schemeId?: Identifier | null;
  custom?: QuoterCustomPlanInput | null;
};

/**
 * `quoter_resolve_plan`: a scheme of the project, or a custom plan. Batch EQ3
 * (TASK-114): the custom plan is ALWAYS offered, to every quoting role, whatever
 * `is_custom_allowed` says (the column stays but is no longer consulted); the
 * quote it produces is created pending approval.
 */
export const resolvePlan = ({
  schemes,
  schemeId,
  custom,
}: ResolvePlanInput): QuoterPlan => {
  if (custom) {
    validateCustomPlan(custom);
    return {
      scheme_id: null,
      scheme_name: "A la medida",
      is_custom: true,
      down_pct: custom.down_pct,
      monthly_pct: custom.monthly_pct,
      deed_pct: custom.deed_pct,
      discount_pct: custom.discount_pct,
      custom_months: custom.monthly_pct > 0 ? (custom.months ?? 0) : 0,
    };
  }
  if (schemeId == null) throw new Error("Elige un esquema de pago");
  const scheme = schemes.find((candidate) => candidate.id === schemeId);
  if (!scheme) throw new Error("No encontramos este esquema de pago");
  if (scheme.archived_at) throw new Error("El esquema de pago está archivado");
  return {
    scheme_id: scheme.id,
    scheme_name: scheme.name,
    is_custom: false,
    down_pct: scheme.down_pct,
    monthly_pct: scheme.monthly_pct,
    deed_pct: scheme.deed_pct,
    discount_pct: scheme.discount_pct,
    custom_months: null,
  };
};

type ComputeInput = {
  project: QuoterProjectData;
  unit: Unit;
  detail: QuoterUnitDetail | undefined;
  /** Every unit of the project with its details (sales count and scarcity). */
  projectUnits: QuoterUnitsData;
  plan: QuoterPlan;
  roofChosen?: boolean;
  /** ISO date the quote is issued on (business "today"). */
  issuedOn: string;
  /** Result of the compliance-pack check, kept here to preserve the SQL order. */
  isProjectSellable: boolean;
};

/**
 * `quoter_compute_for_unit`: computes a quote for a unit at the CURRENT list
 * (read-only). Refuses a sold, blocked, unreleased or non-sellable unit and
 * flags a unit still "por verificar" (`unit_unverified`) instead of refusing
 * it. Adds the scarcity counts ("Quedan X en su etapa / Y en el desarrollo").
 */
export const computeForUnit = ({
  project,
  unit,
  detail,
  projectUnits,
  plan,
  roofChosen,
  issuedOn,
  isProjectSellable,
}: ComputeInput): QuoterPreview => {
  const { config, models, priceLists, listPrices } = project;
  if (!detail) throw new Error("La unidad no tiene datos de cotización");
  if (detail.model_id == null) {
    throw new Error("La unidad no tiene modelo asignado");
  }
  if (!isProjectSellable) {
    throw new Error(
      "Solo puedes cotizar unidades de un proyecto con paquete de cumplimiento validado",
    );
  }
  if (unit.status === "vendida") throw new Error("La unidad ya está vendida");
  if (detail.is_blocked) {
    throw new Error("La unidad está bloqueada y no se puede cotizar");
  }
  const stage = detail.stage ?? null;
  if (
    config.released_stages.length > 0 &&
    (stage == null || !config.released_stages.includes(stage))
  ) {
    throw new Error("La etapa de esta unidad aún no está liberada");
  }
  const model = models.find((candidate) => candidate.id === detail.model_id);
  if (!model) throw new Error("No encontramos este modelo");

  const list = listForSales(priceLists, salesCount(projectUnits.units, config));
  if (list == null) throw new Error("El proyecto no tiene listas de precio");

  const plazo =
    (plan.custom_months ?? 0) > 0
      ? (plan.custom_months as number)
      : config.term_months;
  const validUntil = addDays(issuedOn, config.quote_validity_days);
  const extras = cleanAmount(
    detail.extras.reduce((sum, extra) => sum + (extra.price ?? 0), 0),
  );

  const base = listPrice(model, priceLists, listPrices, list);
  const excess = roundHalfUp(detail.excess_m2 * config.excess_price_per_m2);
  const roof = detail.roof_included || roofChosen ? model.roof_price : 0;
  const gross = base + excess + roof + extras;
  const planResult = buildPlan({
    gross,
    downPct: plan.down_pct,
    monthlyPct: plan.monthly_pct,
    deedPct: plan.deed_pct,
    discountPct: plan.discount_pct,
    plazo,
    issuedOn,
    validUntil,
    apartado: config.apartado_amount,
  });

  const free = availableDetails(projectUnits);
  return {
    list,
    base,
    excess,
    roof,
    extras,
    gross,
    ...planResult,
    unit_id: unit.id,
    unit_identifier: unit.identifier,
    project_id: unit.project_id,
    model_id: model.id,
    plan,
    roof_chosen: Boolean(roofChosen),
    roof_included: detail.roof_included,
    excess_m2: detail.excess_m2,
    unit_unverified: detail.needs_verification,
    protection_days: config.client_protection_days,
    scarcity: {
      stage,
      in_stage:
        stage != null && config.released_stages.includes(stage)
          ? free.filter((row) => row.stage === stage).length
          : null,
      in_development: free.length,
    },
  };
};

/** Details of the units a buyer could still take (disponible and not blocked). */
const availableDetails = ({ units, details }: QuoterUnitsData) =>
  details.filter((detail) => {
    const unit = units.find((candidate) => candidate.id === detail.unit_id);
    return unit?.status === "disponible" && !detail.is_blocked;
  });

/**
 * `reexpedir`: whether the price of a quote reissued at the current list went
 * up, down or stayed.
 */
export const reissueDirection = (
  previousFinal: number,
  newFinal: number,
): "up" | "down" | "same" => {
  if (newFinal > previousFinal) return "up";
  if (newFinal < previousFinal) return "down";
  return "same";
};

/** True when the validity date of a quote has passed (`today` is an ISO date). */
export const isQuoteExpired = (validUntil: string, today: string): boolean =>
  validUntil < today;

/**
 * `quoter_panel`: current list, sales missing for the next list, available
 * units, scarcity per stage and the models comparison at the current list.
 */
export const buildPanel = (
  project: QuoterProjectData,
  projectUnits: QuoterUnitsData,
): QuoterPanel => {
  const { config, models, priceLists, listPrices } = project;
  const counted = salesCount(projectUnits.units, config);
  const current = listForSales(priceLists, counted);
  const next = listThresholds(priceLists).find(
    (threshold) => threshold.sales_needed > counted,
  );
  const free = availableDetails(projectUnits);
  const byStage = new Map<number | null, number>();
  for (const detail of free) {
    const stage = detail.stage ?? null;
    byStage.set(stage, (byStage.get(stage) ?? 0) + 1);
  }
  const scarcity = [...byStage.entries()]
    .sort(([a], [b]) => (a ?? Infinity) - (b ?? Infinity))
    .map(([stage, available]) => ({
      stage,
      available,
      released: stage != null && config.released_stages.includes(stage),
    }));
  const comparison: QuoterModelComparison[] = models
    .filter((model) => !model.archived_at)
    .sort((a, b) => a.sort_order - b.sort_order || Number(a.id) - Number(b.id))
    .map((model) => ({
      model_id: model.id,
      code: model.code,
      name: model.name,
      m2_per_floor: model.m2_per_floor ?? null,
      total_construction: model.total_construction ?? null,
      program: model.program ?? null,
      garage: model.garage ?? null,
      price_from:
        current == null
          ? null
          : listPrice(model, priceLists, listPrices, current),
    }));
  return {
    project_id: config.project_id,
    current_list: current,
    counted_sales: counted,
    apartados_count_toward_list: config.apartados_count_toward_list,
    next_list: next?.list_number ?? null,
    sales_missing_for_next_list: next ? next.sales_needed - counted : null,
    available_units: free.length,
    scarcity,
    models: comparison,
  };
};
